/* CreateInstanceDialogRenderer.js */

"use strict";
const _ = require("lodash");
const ModalUserFeedback = require("@gluenetworks/modal_user_feedback").modalUserFeedback;
let CreateInstanceDialog;
const glueJay = require("@gluenetworks/gluejay").Gluejay;
const Forms = require("@gluenetworks/forms").ui.component;
const ButtonControl = require("@gluenetworks/button_control");
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;

class CreateInstanceDialogRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("create-instance-dialog-gl");
        this._rootElem.classList.add("modal-confirmation-gl");
        CreateInstanceDialog = require("./CreateInstanceDialogComponent");
        this._rootElem.component = this._model;

        this._modalUserFeedback = new ModalUserFeedback();
        this._formsComponent = new Forms(this._model.refResolver);
        this._typeSelector = new ComboBoxControl({
            placeholder: "Instance Type",
            darkTheme: true,
        });
        this._nameField = new InputTextControl({
            darkTheme: true,
            placeholder: "Enter Instance Name",
        });

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on(
            "SelectedTypeChanged",
            (this._onSelectedTypeChanged = this._onSelectedTypeChanged.bind(this))
        );
        this._typeSelector.on(
            "UserValue",
            (this._onTypeChangedByUser = this._onTypeChangedByUser.bind(this))
        );
        this._nameField.on(
            "UserValue",
            (this._onNameChangedByUser = this._onNameChangedByUser.bind(this))
        );
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onPendingPatchesChanged = this._onPendingPatchesChanged.bind(this))
        );
        this._formsComponent.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
        this._formsComponent.on(
            "RefLaunched",
            (this._onRefLaunched = this._onRefLaunched.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeListener("SelectedTypeChanged", this._onSelectedTypeChanged);
        this._typeSelector.removeListener("UserValue", this._onTypeChangedByUser);
        this._nameField.removeListener("UserValue", this._onNameChangedByUser);
        this._formsComponent.removeListener("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._formsComponent.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._formsComponent.removeListener("RefLaunched", this._onRefLaunched);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        let promArr = [
            Promise.resolve(),
            this._typeSelector.shutdown(),
            this._nameField.shutdown(),
        ];

        return Promise.all(promArr)
            .then(() => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                return this._formsComponent.shutdown();
            })
            .then(() => {
                this._formsComponent = undefined;
            });
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            this._titleElem = document.createElement("h2");
            this._formsElem = this._formsComponent.renderer.rootElement;
            this._actionsContainer = document.createElement("div");
            this._actionsContainer.classList.add("actions_container");

            let contianerElem = document.createElement("div");
            contianerElem.classList.add(
                "create-instance-container",
                "modal-confirmation-gl",
                "modal_feedback_container"
            );

            this._rootElem.appendChild(this._modalUserFeedback.renderer.rootElement);
            this._rootElem.appendChild(contianerElem);
            contianerElem.appendChild(this._titleElem);
            contianerElem.appendChild(this._typeSelector.renderer.rootElement);
            contianerElem.appendChild(this._nameField.renderer.rootElement);
            contianerElem.appendChild(this._formsElem);
            contianerElem.appendChild(this._actionsContainer);
        }

        return Promise.resolve()
            .then(() => {
                return this._renderTitle();
            })
            .then(() => {
                return this._renderActions();
            })
            .then(() => {
                return this._renderTypes();
            })
            .then(() => {
                return this._renderForm();
            });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    _renderTitle() {
        this._titleElem.innerText = this._model.title;
    }
    _renderTypes() {
        //render the options
        let types = this._model.typePaths;
        let options = [];
        types.forEach((type) => {
            let explodedPath = glueJay.parsePath(type);
            let label = _.get(explodedPath, "typeName", type);

            options.push({
                label: label,
                value: type,
            });
        });
        this._typeSelector.setOptions(options);

        //set the selected option
        let selectedType = this._model.selectedType;
        let selectorValue = this._typeSelector.getValue();
        if (!selectedType) {
            if (!_.isEmpty(options)) {
                let defaultSelectedType = options[0].value;
                this._model.setSelectedType(defaultSelectedType, true);
                this._typeSelector.setValue(defaultSelectedType);
            }
        } else if (selectorValue !== selectedType) {
            this._typeSelector.setValue(selectedType);
        }
    }
    _renderForm() {
        let selectedType = this._model.getSelectedType();

        return Promise.resolve()
            .then(() => {
                if (selectedType) {
                    return this._model.retrieveType(selectedType);
                }
            })
            .then((jtype) => {
                if (jtype) {
                    this._nameField.show();
                    this._formsComponent.show();
                    this._formsComponent.setTypeInformation(jtype, true);
                    this._formsComponent._setPristineValue({}, true);
                    return this._formsComponent.renderer.render();
                } else {
                    this._nameField.hide();
                    this._formsComponent.hide();
                }
            })
            .catch((e) => {
                this._modalUserFeedback.displayError({
                    error: e,
                });
            });
    }
    _renderActions() {
        if (!this._actionsContainer.hasChildNodes()) {
            let confRenderResults = this._renderAction(this._model.confirmationDeets);
            let confElem = confRenderResults.elem;
            this._confirmBtn = confRenderResults.cmpnt;
            this._confirmBtn.setActive();
            this._onNameChangedByUser(this._nameField.getValue());
            confElem.classList.add("confirmation_action");
            confElem.addEventListener("click", () => {
                let selectedType = this._model.getSelectedType();
                if (selectedType) {
                    Promise.resolve()
                        .then(() => {
                            this._displayLoading();

                            let value = this._formsComponent.getValue();
                            let explodedPath = glueJay.parsePath(selectedType);
                            let path = `/org/instance/construct/${explodedPath.construct}`;
                            if (explodedPath.ignored) {
                                path += "/" + explodedPath.ignored;
                            }
                            path += `/${explodedPath.typeName}/${this._nameField.getValue()}.json`;

                            return this._model.saveInstance(path, value);
                        })
                        .then((saveResult) => {
                            this._model.confirm({
                                saveResult: saveResult,
                            });
                        })
                        .catch((e) => {
                            return this._displayError(e).then(() => {
                                this._model.reject(e);
                            });
                        });
                }
            });
            let rejectRenderResults = this._renderAction(this._model.rejectionDeets);
            let rejectElem = rejectRenderResults.elem;
            rejectElem.classList.add("rejection_action");
            rejectElem.addEventListener("click", () => {
                this._model.reject();
            });

            this._actionsContainer.appendChild(rejectElem);
            this._actionsContainer.appendChild(confElem);
        }
    }
    _renderAction(deets) {
        let actionElem;
        let btn;
        switch (deets.type) {
            case "text":
                btn = new ButtonControl({
                    label: deets.text,
                    darkTheme: true,
                });
                actionElem = btn.renderer.rootElement;
                break;
            case "destructive":
                btn = new ButtonControl({
                    label: deets.text,
                    darkTheme: true,
                });
                actionElem = btn.renderer.rootElement;
                actionElem.classList.add("destructive");
                break;
            case "additive":
                btn = new ButtonControl({
                    label: deets.text,
                    darkTheme: true,
                });
                actionElem = btn.renderer.rootElement;
                actionElem.classList.add("additive");
                break;
        }

        if (deets.classes) {
            let classStr = deets.classes;
            if (_.isString(deets.classes)) {
                classStr = deets.classes.split(" ");
            }
            actionElem.classList.add.apply(actionElem.classList, classStr);
            actionElem.classList.add("action");
        }

        return {
            cmpnt: btn,
            elem: actionElem,
        };
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onRefLaunched(deets) {
        let dialog = new CreateInstanceDialog(
            this._model.refResolver,
            this._model.nibApiService,
            this._model.userSession,
            {
                title: "Create An Instance",
                refLaunchedDetails: deets,
                selectedType: null,
                instanceData: null,
            }
        );
        return this._modalUserFeedback
            .displayCustom(dialog, {})
            .then((/*value*/) => {
                //TODO anyway to autoselect a ref???
                return this._renderForm();
            })
            .catch((e) => {
                return Promise.reject({
                    errorCode: "CreateInstanceDialogFailed",
                    errorMsg: "Nested Create Instance Dialog failed.",
                    error: e,
                });
            });
    }
    _onSelectedTypeChanged() {
        this.render();
    }
    _onTypeChangedByUser() {
        this._model.setSelectedType(this._typeSelector.getValue());
    }
    _onNameChangedByUser(value) {
        if (!_.isEmpty(value)) {
            this._confirmBtn.show();
        } else {
            this._confirmBtn.hide();
        }
    }
    _onPendingPatchesChanged() {
        // console.log("Patches Changed",this._formsComponent.getPendingPatches());
        // console.log("New Value is ",this._formsComponent.getValue());
    }
    _onDirtyStateChanged() {
        // console.log("Dirty State Changed",this._formsComponent.isDirty());
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _displayLoading() {
        return this._modalUserFeedback.displayLoading();
    }
    _displayError(error) {
        return this._modalUserFeedback.displayError(error);
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = CreateInstanceDialogRenderer;
